/* ═══════════════════════════════════════════════════════════════════════
   GRG Team Order — captain dashboard client
   Handles all 3 collection modes (share, fill, csv), MOU + bank capture.

   Two modes:
     • Live  — hits Cian's app API (POST /public/team-order/*)
     • Demo  — pure-client mock, activated by ?demo=1 on the URL.
               Lets us show the flow to the client before app-side lands.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const root = document.querySelector('[data-tofm-root]');
  if (!root) return;

  const API_BASE = root.dataset.apiBase || '';
  const params   = new URLSearchParams(window.location.search);
  const rawToken = params.get('token') || '';
  const rawRow   = params.get('row')   || '';
  // Legacy /pages/team-order-fill?token=<row_token> — the "fill" section
  // stamps data-tofm-legacy-fill on root. Re-interpret ?token= as a row_token.
  const LEGACY_FILL = !!root.dataset.tofmLegacyFill;
  const rawJoin  = params.get('join')  || '';
  // A player who already claimed a row on this device keeps their private
  // row_token, so returning to the shared link edits their row instead of
  // claiming a second one.
  const JOIN_STORE = 'grg-tofm-row:' + rawJoin;
  let claimedRow = '';
  if (rawJoin) {
    try { claimedRow = window.localStorage.getItem(JOIN_STORE) || ''; } catch (_) {}
  }
  const JOIN     = rawJoin;
  const ROW      = LEGACY_FILL ? rawToken : (rawRow || claimedRow);
  const token    = LEGACY_FILL ? '' : rawToken;
  const DEMO     = params.get('demo') === '1' || (!token && !ROW && !JOIN);
  // ?join= shows the same single-player form; it just claims a row first.
  const VIEW     = (ROW || JOIN) ? 'player' : 'captain';

  // ── State ────────────────────────────────────────────
  const state = {
    club:      'Strokstown GAA Club',
    sport:     'GAA',
    deadline:  '2026-09-18',
    status:    'draft',
    sponsors:  ['Atlantic Fire', 'RV Timber Harvesting LTD'],
    designs:   [],
    products:  [
      // Sizes mirror /pages/sizing-guide (canonical GRG size grid).
      { key: 'jersey', label: 'Training Jersey', sizes: ['1/2 YRS','3/4 YRS','5/6 YRS','7/8 YRS','9/10 YRS','11/12 YRS','13/14 YRS','XS','S','M','L','XL','2XL','3XL','4XL','5XL'] },
      { key: 'shorts', label: 'Playing Shorts',  sizes: ['16"','18"','20"','22"','24"','26"','28"','30"','32"','34"','36"','38"'] },
      { key: 'socks',  label: 'Socks',           sizes: ['4-7','7-9','9-11'] }
    ],
    rows:      [],   // team_order_row items (as the server has them)
    draft:     [],   // captain's in-progress roster edits (fill-form mode)
    mou_text:  null,
    api_ok:    false
  };

  // ── UI helpers ───────────────────────────────────────
  const $  = (sel, base) => (base || root).querySelector(sel);
  const $$ = (sel, base) => (base || root).querySelectorAll(sel);
  const el = (tag, attrs, ...kids) => {
    const n = document.createElement(tag);
    if (attrs) for (const k in attrs) {
      if (k === 'class') n.className = attrs[k];
      else if (k === 'html') n.innerHTML = attrs[k];
      else if (k.startsWith('on')) n.addEventListener(k.slice(2), attrs[k]);
      else n.setAttribute(k, attrs[k]);
    }
    for (const c of kids) {
      if (c == null) continue;
      n.append(c.nodeType ? c : document.createTextNode(c));
    }
    return n;
  };

  // Bind a click handler if the element exists; silent no-op otherwise.
  // Used so the captain-only listeners don't throw on the player-view page.
  const on = (sel, evt, fn) => { const n = $(sel); if (n) n.addEventListener(evt, fn); };

  const notice = (msg, kind) => {
    const n = $('[data-tofm-notice]');
    if (!n) return;
    n.className = 'tofm__notice tofm__notice--' + (kind || 'info');
    n.textContent = msg;
    n.hidden = !msg;
  };

  // ── API layer (real + mock) ──────────────────────────
  async function api(path, opts) {
    if (DEMO) return mockApi(path, opts);
    opts = opts || {};
    // Player view uses the row_token; captain view uses the captain token.
    // A join-scoped call uses the shared joinToken; once the player has
    // claimed a row, their private row_token takes over.
    const activeToken = path.indexOf('/join') !== -1 ? JOIN
                      : (VIEW === 'player' ? (ROW || JOIN) : token);
    const url = API_BASE + path + (path.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(activeToken);
    const init = { method: opts.method || 'GET', headers: opts.headers || {} };
    if (opts.body !== undefined) init.body = opts.body;
    if (!opts._multipart && init.body && typeof init.body === 'string') {
      init.headers['content-type'] = init.headers['content-type'] || 'application/json';
    }
    const res = await fetch(url, init);
    if (!res.ok) {
      let body = {};
      try { body = await res.json(); } catch (_) {}
      const err = new Error(body.error || ('API ' + res.status));
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  function mockApi(path, opts) {
    // Local-only, non-persistent — everything lives in `state`.
    return new Promise((resolve) => setTimeout(() => {
      if (path.startsWith('/public/team-order?') || path === '/public/team-order') {
        resolve({ order: {
          club_name:     state.club,
          sport:         state.sport,
          deadline_at:   state.deadline,
          status:        state.status,
          sponsors:      state.sponsors,
          designs:       state.designs,
          products:      state.products,
          rows:          state.rows,
          mou_text:      MOU_TEMPLATE(state)
        }});
      } else if (path === '/public/team-order/rows/bulk') {
        const body = JSON.parse(opts.body);
        // Mirror the real contract: the server mints id + row_token, and the
        // client builds share links from the response.
        state.rows = (body.rows || []).map((r, i) => Object.assign({}, r, {
          id:        r.id || cryptoId(),
          row_token: r.row_token || (cryptoId() + cryptoId()),
          idx:       r.idx != null ? r.idx : i + 1
        }));
        resolve({ ok: true, rows: state.rows });
      } else if (path === '/public/team-order/row' && (!opts || !opts.method || opts.method === 'GET')) {
        // Player-view fetch: return a mock row + order meta
        const row = state.rows.find(r => r.row_token === ROW) || { id: 'demo-row', idx: 7, player_name: '', player_initials: '', items: [] };
        resolve({ row, order: {
          club_name: state.club, deadline_at: state.deadline,
          products: state.products, sponsors: state.sponsors
        }});
      } else if (path === '/public/team-order/row') {
        const body = JSON.parse(opts.body);
        const idx = state.rows.findIndex(r => r.id === body.id);
        if (idx >= 0) state.rows[idx] = Object.assign(state.rows[idx], body);
        else state.rows.push(Object.assign({ id: cryptoId() }, body));
        resolve({ ok: true });
      } else if (path === '/public/team-order/mou') {
        state.status = 'awaiting-deposit';
        resolve({ ok: true, signed_at: new Date().toISOString() });
      } else if (path === '/public/team-order/bank') {
        resolve({ ok: true });
      } else if (path === '/public/team-order/csv') {
        resolve({ ok: true, rows_created: 0 });
      } else {
        resolve({ ok: true });
      }
    }, 250));
  }

  // Demo-mode only: mockApi plays the part of the server, which is the sole
  // minter of ids and row_tokens. Never use this for anything sent to the API —
  // Math.random() is not suitable for an access credential.
  const cryptoId = () => 'r_' + Math.random().toString(36).slice(2, 10);

  // ── Initial load ─────────────────────────────────────
  async function boot() {
    // Toggle correct view scaffold FIRST so hydrate() finds its DOM
    const capV = $('[data-tofm-view="captain"]');
    const plrV = $('[data-tofm-view="player"]');
    if (capV) capV.hidden = (VIEW !== 'captain');
    if (plrV) plrV.hidden = (VIEW !== 'player');

    try {
      if (JOIN && !ROW) {
        // Shared squad link, row not yet claimed: fetch order info only.
        const data = await api('/public/team-order/join');
        hydrateJoin(data.order || {});
      } else if (VIEW === 'player') {
        const data = await api('/public/team-order/row');
        hydratePlayer(data.row || {}, data.order || {});
      } else {
        const { order } = await api('/public/team-order');
        hydrate(order);
      }
      state.api_ok = true;
    } catch (e) {
      // Fall back to demo mode silently if the API is unreachable
      if (JOIN && !ROW) {
        // Endpoints not live yet — say so plainly instead of a broken form.
        const data = await mockApi('/public/team-order/row');
        hydrateJoin(data.order);
        if (!DEMO) {
          notice('The shared squad link is not active yet. Ask your captain for your personal link, or try again later.', 'warn');
          const f = $('[data-tofm-player-form]');
          if (f) f.hidden = true;
        }
      } else if (VIEW === 'player') {
        const data = await mockApi('/public/team-order/row');
        hydratePlayer(data.row, data.order);
      } else {
        const { order } = await mockApi('/public/team-order');
        hydrate(order);
      }
      notice('Preview mode — the app backend is not connected yet. Actions won\'t persist.', 'warn');
    }
    $('[data-tofm-loading]').hidden = true;
    $('[data-tofm-app]').hidden = false;
  }

  function hydrate(order) {
    Object.assign(state, order);
    // The API speaks club_name/deadline_at; the MOU template reads club/deadline.
    // Without this the MOU renders the hardcoded demo club on live orders.
    if (order.club_name)   state.club     = order.club_name;
    if (order.deadline_at) state.deadline = order.deadline_at;
    $('[data-tofm-club]').textContent = order.club_name + ' — Team Order';
    const d = $('[data-tofm-deadline]');
    d.textContent = 'Deadline: ' + order.deadline_at;
    d.hidden = false;
    const s = $('[data-tofm-status]');
    s.textContent = order.status.replace(/-/g, ' ');
    s.className = 'tofm__status tofm__status--' + order.status;
    s.hidden = false;

    // Sponsors
    const sponsorList = $('[data-tofm-sponsors]');
    sponsorList.innerHTML = '';
    (order.sponsors || []).forEach(sp => sponsorList.append(el('li', null, sp)));

    // Shared squad link — one URL for everyone. Only shown when the app
    // supplies a join_token; otherwise the captain uses per-player links.
    const joinWrap = $('[data-tofm-join-wrap]');
    if (joinWrap) {
      if (order.join_token) {
        const u = new URL('/pages/team-order', window.location.origin);
        u.searchParams.set('join', order.join_token);
        if (DEMO) u.searchParams.set('demo', '1');
        $('[data-tofm-join-url]').value = u.toString();
        joinWrap.hidden = false;
        $('[data-tofm-join-missing]').hidden = true;
      } else {
        joinWrap.hidden = true;
        $('[data-tofm-join-missing]').hidden = false;
      }
    }

    // Restore a previously chosen delivery method + address.
    if (order.fulfilment_method) {
      const r = $('[data-tofm-fulfilment][value="' + order.fulfilment_method + '"]');
      if (r) r.checked = true;
    }
    const savedAddr = order.delivery_address;
    if (savedAddr && typeof savedAddr === 'object') {
      $$('[data-tofm-addr]').forEach(inp => {
        const v = savedAddr[inp.dataset.tofmAddr];
        if (v) inp.value = v;
      });
    }
    syncFulfilment();

    renderAgreed('[data-tofm-agreed]', '[data-tofm-agreed-wrap]');

    // Designs
    const designs = $('[data-tofm-designs]');
    designs.innerHTML = '';
    if (!order.designs || !order.designs.length) {
      designs.append(el('span', { style: 'color:#6f6f6f;font-size:.85rem;' }, 'No design attachments yet.'));
    } else {
      order.designs.forEach(url => designs.append(el('img', { src: url, alt: 'Design attachment' })));
    }
  }

  // ── Mode picker ──────────────────────────────────────
  root.addEventListener('click', (ev) => {
    const modeCard = ev.target.closest('[data-tofm-mode]');
    if (!modeCard) return;
    const mode = modeCard.dataset.tofmMode;
    $$('[data-tofm-mode]').forEach(c => c.classList.toggle('tofm__mode-card--active', c === modeCard));
    $$('[data-tofm-panel]').forEach(p => p.hidden = p.dataset.tofmPanel !== mode);
    $('[data-tofm-mou]').hidden = false;
    renderMou();
    if (mode === 'share') {
      // Rows already exist on the order (captain returning to the page) —
      // show them instead of an empty panel that invites a re-generate,
      // which would replace every row and orphan links already sent out.
      if (!$$('[data-tofm-share-rows] tr').length && state.rows.some(r => r.row_token)) {
        renderShareRows();
        $('[data-tofm-share-table]').hidden = false;
      $('[data-tofm-refresh-rows]').hidden = false;
      }
    } else if (mode === 'fill') {
      renderFillHeader();
      // Seed the draft from rows already saved on the order.
      if (!state.draft.length) {
        state.draft = state.rows.length
          ? state.rows.map(r => ({
              id: r.id || '', idx: r.idx,
              player_name: r.player_name || '', player_initials: r.player_initials || '',
              items: (r.items || []).map(it => Object.assign({}, it))
            }))
          : [blankRow(1)];
      }
      renderFillRows();
    } else if (mode === 'csv') {
      // nothing to prep
    }
  });

  // ── SHARE-LINK mode ──────────────────────────────────
  on('[data-tofm-generate-rows]', 'click', async () => {
    const n = parseInt($('#tofm-count').value, 10) || 15;
    const btn = $('[data-tofm-generate-rows]');
    // The SERVER mints row ids and row_tokens — a share link is an access
    // credential, so the client must never invent one. Send idx/player_name
    // only; anything else is refused with a 400.
    // Re-generating REPLACES every row: existing links stop working and any
    // player data already submitted is lost. Confirm before destroying it.
    const existing = state.rows.filter(r => r.row_token);
    if (existing.length) {
      const filled = existing.filter(r => r.player_name).length;
      const warn = 'You already have ' + existing.length + ' player link(s)'
        + (filled ? ', ' + filled + ' of them already filled in' : '')
        + '.\n\nGenerating new links REPLACES them all — every link you have already sent will stop working'
        + (filled ? ' and the ' + filled + ' completed row(s) will be lost' : '')
        + '.\n\nContinue?';
      if (!window.confirm(warn)) return;
    }
    const draft = Array.from({ length: n }, (_, i) => ({ idx: i + 1, player_name: '' }));
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = 'Generating…';
    try {
      const res = await api('/public/team-order/rows/bulk', {
        method: 'POST', body: JSON.stringify({ rows: draft })
      });
      // Build the share links from what came back, never from local strings.
      state.rows = res.rows || [];
      renderShareRows();
      $('[data-tofm-share-table]').hidden = false;
      $('[data-tofm-refresh-rows]').hidden = false;
      notice(state.rows.length + ' player link(s) ready. Copy each one and send it to that player.', 'success');
    } catch (e) {
      // Previously fire-and-forget: links appeared even when the save failed,
      // and players hit dead URLs. Surface it instead.
      $('[data-tofm-share-table]').hidden = true;
      if (e.status === 409) notice('Order is locked — MOU already signed.', 'warn');
      else notice('Could not create the player links: ' + (e.message || 'unknown error') + ' — nothing was saved, please try again.', 'error');
    } finally {
      btn.disabled = false;
      btn.textContent = label;
    }
  });

  on('[data-tofm-join-copy]', 'click', (ev) => {
    copyLink(ev.currentTarget, $('[data-tofm-join-url]').value);
  });

  // Captain polls for player submissions without re-generating anything.
  on('[data-tofm-refresh-rows]', 'click', async () => {
    const btn = $('[data-tofm-refresh-rows]');
    const label = btn.textContent;
    btn.disabled = true; btn.textContent = 'Refreshing…';
    try {
      const { order } = await api('/public/team-order');
      hydrate(order);
      renderShareRows();
      const done = state.rows.filter(r => r.player_name || r.filled_by === 'player_self').length;
      notice(done + ' of ' + state.rows.length + ' player(s) have filled in their details.', done ? 'success' : 'info');
    } catch (e) {
      notice('Could not refresh: ' + (e.message || 'unknown error'), 'error');
    } finally {
      btn.disabled = false; btn.textContent = label;
    }
  });

  function renderShareRows() {
    const tbody = $('[data-tofm-share-rows]');
    tbody.innerHTML = '';
    state.rows.forEach(r => {
      // The server marks a genuine player submission with filled_by:'player_self';
      // a name alone also counts (captain may have typed it in).
      const filled = !!r.player_name || r.filled_by === 'player_self';
      // Consolidated page: same URL as captain, but with ?row=<row_token>.
      // The JS flips into player-view when it sees ?row=.
      const shareUrl = new URL('/pages/team-order', window.location.origin);
      shareUrl.searchParams.set('row', r.row_token);
      if (DEMO) shareUrl.searchParams.set('demo', '1');
      const btn = el('button', {
        class: 'tofm__roster-copy-btn',
        type: 'button',
        onclick: (e) => copyLink(e.currentTarget, shareUrl.toString())
      }, 'Copy link');
      tbody.append(el('tr', null,
        el('td', null, String(r.idx)),
        el('td', null, r.player_name || el('span', { style: 'color:#a0a3ac;' }, '— unfilled —')),
        el('td', null, el('span', {
          class: 'tofm__roster-badge tofm__roster-badge--' + (filled ? 'filled' : 'unfilled')
        }, filled ? 'Filled' : 'Waiting')),
        el('td', null, btn)
      ));
    });
  }

  function copyLink(btn, url) {
    try {
      navigator.clipboard.writeText(url);
      // Restore whatever label this button had — the squad-link button and
      // the per-row buttons don't share the same text.
      const prev = btn.textContent;
      btn.textContent = 'Copied!';
      btn.classList.add('is-copied');
      setTimeout(() => { btn.textContent = prev; btn.classList.remove('is-copied'); }, 1600);
    } catch (_) {
      window.prompt('Copy this link:', url);
    }
  }

  // ── FILL-FORM mode ───────────────────────────────────
  // ── Roster overview + per-player modal ───────────────
  // A wide table can't hold N products x their add-ons, so the table is a
  // compact overview and each row opens a modal with that player's full
  // detail. state.draft holds edits until Save Draft persists them.

  // ── Captain-level add-ons (read-only) ────────────────
  // Agreed by GRG with the captain for the whole order. Never rendered as
  // inputs — the server rejects them on player endpoints by design.
  function captainAddons(p) {
    return Array.isArray(p.captain_addons) ? p.captain_addons : [];
  }

  // A captain add-on only has something to show once we know the CHOSEN
  // value. `bool` is self-describing (ticked = yes). `select`/`text` need a
  // `value` from the app; without one we show nothing rather than guess —
  // printing "Interlock 180 GSM" when the squad gets "Heavy Light" is worse
  // than printing nothing.
  function captainAddonValue(a) {
    if (a.type === 'bool') {
      return (a.value === true || a.value === 'true' || a.value === undefined) ? 'Yes' : '';
    }
    return a.value == null ? '' : String(a.value).trim();
  }

  function renderAgreed(targetSel, wrapSel) {
    const host = $(targetSel);
    const wrap = $(wrapSel);
    if (!host || !wrap) return;
    const esc = (v) => String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    let html = '';
    (state.products || []).forEach(p => {
      const rows = captainAddons(p)
        .map(a => ({ label: a.label || a.key, value: captainAddonValue(a) }))
        .filter(r => r.value !== '');
      // Skip products with no captain-level choices (socks, beanies, …).
      if (!rows.length) return;
      html += '<div class="tofm__agreed-group">'
        + '<h4 class="tofm__agreed-product">' + esc(p.label) + '</h4>'
        + '<dl class="tofm__agreed-list">'
        + rows.map(r => '<dt>' + esc(r.label) + '</dt><dd>' + esc(r.value) + '</dd>').join('')
        + '</dl></div>';
    });

    host.innerHTML = html;
    wrap.hidden = !html;
  }

  function productAddons(p) {
    // Add-ons are defined per product by the app. Falls back to none, so
    // the UI degrades cleanly until the backend ships them.
    return Array.isArray(p.addons) ? p.addons : [];
  }

  // Colour band for an add-on pill — mirrors the app admin so the roster
  // reads the same on both sides.
  function addonPillClass(key) {
    const k = String(key || '').toLowerCase();
    if (/_material$|^material$/.test(k))                     return 'tofm__pill--material';
    if (/^pattern$/.test(k))                                 return 'tofm__pill--pattern';
    if (/neck|^type$/.test(k))                               return 'tofm__pill--neck';
    if (/logo|stripe|embroider/.test(k))                     return 'tofm__pill--logo';
    return 'tofm__pill--player';
  }

  // Which add-ons this player has actually filled in, across all products.
  function rowAddonPills(row) {
    const out = [];
    (state.products || []).forEach(p => {
      const it = itemFor(row, p);
      if (!it || !it.addons) return;
      productAddons(p).forEach(a => {
        const v = it.addons[a.key];
        if (v === undefined || v === '' || v === false) return;
        out.push({ label: a.label || a.key, cls: addonPillClass(a.key) });
      });
    });
    return out;
  }

  function blankRow(idx) {
    return { id: '', idx: idx, player_name: '', player_initials: '', items: [] };
  }

  // Items are keyed by product `key`; older rows may carry the label.
  function itemFor(row, p) {
    return (row.items || []).find(it => it.product === p.key || it.product === p.label);
  }

  function filledCount(row) {
    return state.products.filter(p => { const it = itemFor(row, p); return it && it.size; }).length;
  }

  function renderFillHeader() {
    const thead = $('[data-tofm-fill-thead]');
    if (!thead) return;
    thead.innerHTML = '<tr>'
      + '<th style="width:44px;">#</th>'
      + '<th>Player name</th>'
      + '<th style="width:110px;">Initials</th>'
      + '<th style="width:140px;">Products</th>'
      + '<th style="min-width:160px;">Add-ons</th>'
      + '<th style="width:96px;"></th>'
      + '<th style="width:44px;"></th>'
      + '</tr>';
  }

  function renderFillRows() {
    const tbody = $('[data-tofm-fill-rows]');
    if (!tbody) return;
    tbody.innerHTML = '';
    const total = state.products.length;
    state.draft.forEach((row, i) => {
      const done = filledCount(row);
      const complete = total > 0 && done === total;
      const tr = el('tr', { 'data-draft-index': String(i) });
      tr.append(el('td', null, String(i + 1)));
      tr.append(el('td', null, row.player_name
        || el('span', { style: 'color:#a0a3ac;' }, '— not filled in —')));
      tr.append(el('td', null, row.player_initials || '—'));
      tr.append(el('td', null, el('span', {
        class: 'tofm__roster-badge tofm__roster-badge--' + (complete ? 'filled' : 'unfilled')
      }, done + ' of ' + total + (complete ? ' ✓' : ''))));
      // Add-ons this player has filled, as colour-coded pills.
      const pillCell = el('td');
      const pills = rowAddonPills(row);
      if (pills.length) {
        const box = el('span', { class: 'tofm__pills' });
        pills.forEach(pl => box.append(el('span', { class: 'tofm__pill ' + pl.cls }, pl.label)));
        pillCell.append(box);
      } else {
        pillCell.append(el('span', { style: 'color:#a0a3ac;' }, '—'));
      }
      tr.append(pillCell);

      tr.append(el('td', null, el('button', {
        class: 'tofm__roster-copy-btn', type: 'button',
        onclick: (ev) => { ev.stopPropagation(); openPlayerModal(i); }
      }, 'Edit')));
      tr.append(el('td', null, el('button', {
        class: 'tofm__roster-copy-btn tofm__roster-remove', type: 'button',
        'aria-label': 'Remove this player', title: 'Remove this player',
        onclick: (ev) => {
          ev.stopPropagation();
          state.draft.splice(i, 1);
          renderFillRows();
        }
      }, '×')));
      tr.addEventListener('click', () => openPlayerModal(i));
      tbody.append(tr);
    });
  }

  // Kept for the CSV path, which seeds rows straight into the draft.
  function addFillRow(seed) {
    state.draft.push(seed || blankRow(state.draft.length + 1));
    renderFillRows();
  }

  // ── Player modal ─────────────────────────────────────
  let modalIndex = -1;

  function openPlayerModal(i) {
    if (i < 0 || i >= state.draft.length) return;
    modalIndex = i;
    const row = state.draft[i];
    const m = $('[data-tofm-modal]');
    if (!m) return;

    $('[data-tofm-modal-title]').textContent = 'Player ' + (i + 1) + ' of ' + state.draft.length;
    $('[data-tofm-modal-name]').value = row.player_name || '';
    $('[data-tofm-modal-initials]').value = row.player_initials || '';

    const wrap = $('[data-tofm-modal-products]');
    wrap.innerHTML = '';
    if (!state.products.length) {
      wrap.append(el('p', { class: 'tofm__mode-card-desc' }, 'No products on this order yet.'));
    }
    state.products.forEach((p, pi) => {
      const it = itemFor(row, p) || {};
      const addons = productAddons(p);
      const card = el('div', { class: 'tofm__pcard', 'data-product-key': p.key });

      // Header: product name + its size picker, always visible.
      const head = el('div', { class: 'tofm__pcard-head' });
      head.append(el('span', { class: 'tofm__pcard-name' }, p.label));
      const sizeSel = el('select', { class: 'tofm__input tofm__pcard-size', 'data-size-for': p.key });
      sizeSel.innerHTML = '<option value="">Choose size…</option>'
        + (p.sizes || []).map(sz => '<option' + (sz === it.size ? ' selected' : '') + '>' + sz + '</option>').join('');
      head.append(sizeSel);
      card.append(head);

      // Add-ons for this product, if the order defines any.
      if (addons.length) {
        const body = el('div', { class: 'tofm__pcard-addons' });
        const chosen = it.addons || {};
        addons.forEach(a => {
          const f = el('div', { class: 'tofm__pcard-addon' });
          const id = 'ad-' + pi + '-' + a.key;
          if (a.type === 'bool') {
            const lab = el('label', { class: 'tofm__check', for: id });
            const cb = el('input', { type: 'checkbox', id: id, 'data-addon-for': p.key, 'data-addon-key': a.key });
            if (chosen[a.key]) cb.checked = true;
            lab.append(cb, el('span', null, a.label));
            f.append(lab);
          } else if (a.type === 'select') {
            f.append(el('span', { class: 'tofm__label' }, a.label));
            const sel = el('select', { class: 'tofm__input', id: id, 'data-addon-for': p.key, 'data-addon-key': a.key });
            sel.innerHTML = '<option value="">—</option>'
              + (a.options || []).map(o => '<option' + (o === chosen[a.key] ? ' selected' : '') + '>' + o + '</option>').join('');
            f.append(sel);
          } else {
            f.append(el('span', { class: 'tofm__label' }, a.label));
            const inp = el('input', {
              type: 'text', class: 'tofm__input', id: id,
              'data-addon-for': p.key, 'data-addon-key': a.key,
              value: chosen[a.key] || ''
            });
            if (a.max) inp.setAttribute('maxlength', String(a.max));
            if (a.placeholder) inp.setAttribute('placeholder', a.placeholder);
            f.append(inp);
          }
          body.append(f);
        });
        card.append(body);
      }
      wrap.append(card);
    });

    $('[data-tofm-modal-prev]').disabled = (i === 0);
    $('[data-tofm-modal-next]').disabled = (i === state.draft.length - 1);
    m.hidden = false;
    document.body.style.overflow = 'hidden';
    const n = $('[data-tofm-modal-name]');
    if (n) n.focus();
  }

  function collectModal() {
    if (modalIndex < 0) return;
    const row = state.draft[modalIndex];
    if (!row) return;
    row.player_name = $('[data-tofm-modal-name]').value.trim();
    row.player_initials = $('[data-tofm-modal-initials]').value.trim();
    const items = [];
    state.products.forEach(p => {
      const sel = $('[data-size-for="' + cssEscape(p.key) + '"]');
      const size = sel ? sel.value : '';
      const addons = {};
      $$('[data-addon-for="' + cssEscape(p.key) + '"]').forEach(inp => {
        const k = inp.dataset.addonKey;
        const v = inp.type === 'checkbox' ? inp.checked : inp.value.trim();
        if (v !== '' && v !== false) addons[k] = v;
      });
      // Keep a product only when it has a size or at least one add-on set.
      if (size || Object.keys(addons).length) {
        const entry = { product: p.key, size: size };
        if (Object.keys(addons).length) entry.addons = addons;
        items.push(entry);
      }
    });
    row.items = items;
  }

  // Product keys are slugs, but escape anyway so a stray character in an
  // app-supplied key can't break the selector.
  function cssEscape(v) {
    return String(v).replace(/["\\]/g, '\\$&');
  }

  function closeModal() {
    const m = $('[data-tofm-modal]');
    if (m) m.hidden = true;
    document.body.style.overflow = '';
    modalIndex = -1;
  }

  on('[data-tofm-modal-save]', 'click', () => { collectModal(); renderFillRows(); closeModal(); });
  on('[data-tofm-modal-close]', 'click', () => { closeModal(); renderFillRows(); });
  on('[data-tofm-modal-prev]', 'click', () => { collectModal(); openPlayerModal(modalIndex - 1); renderFillRows(); });
  on('[data-tofm-modal-next]', 'click', () => { collectModal(); openPlayerModal(modalIndex + 1); renderFillRows(); });
  on('[data-tofm-modal-backdrop]', 'click', () => { collectModal(); renderFillRows(); closeModal(); });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && !$('[data-tofm-modal]').hidden) { collectModal(); renderFillRows(); closeModal(); }
  });

  on('[data-tofm-fill-add]', 'click', () => addFillRow());
  on('[data-tofm-fill-save]', 'click', async () => {
    if (modalIndex >= 0) collectModal();
    const rows = state.draft.map((r, i) => {
      const out = { idx: i + 1, player_name: r.player_name || '',
                    player_initials: r.player_initials || '', items: r.items || [] };
      // Only send an id the SERVER gave us; omit it for new rows.
      if (r.id) out.id = r.id;
      return out;
    });
    try {
      const res = await api('/public/team-order/rows/bulk', { method: 'POST', body: JSON.stringify({ rows }) });
      // Adopt server-minted ids so the next save updates instead of duplicating.
      if (res && res.rows) {
        state.rows = res.rows;
        res.rows.forEach((r, i) => { if (state.draft[i]) state.draft[i].id = r.id; });
      }
      renderFillRows();
      notice('Draft saved. You can come back to this URL to keep editing until you sign the MOU.', 'success');
    } catch (e) {
      if (e.status === 409) notice('Order is locked — MOU already signed.', 'warn');
      else notice('Save failed: ' + (e.message || 'unknown error'), 'error');
    }
  });

  // ── CSV mode ─────────────────────────────────────────
  // "Training Jersey" + "Jersey number" would read "Training Jersey Jersey
  // number"; drop the repeated word so headers stay readable.
  function addonColumn(p, a) {
    const label = a.label.replace(/^jersey\s+/i, '');
    return p.label + ' ' + label.charAt(0).toUpperCase() + label.slice(1);
  }





  // The SERVER builds the template — it's the single source of truth, and
  // the Geevagh layout (bordered per-garment tables, merged sponsor rows,
  // age-group blocks side by side) needs a real .xlsx, which CSV can't hold.
  on('[data-tofm-csv-download]', 'click', async (e) => {
    e.preventDefault();
    const btn = $('[data-tofm-csv-download]');
    const label = btn.textContent;
    btn.textContent = 'Preparing…';
    try {
      const url = API_BASE + '/public/team-order/csv-template?token=' + encodeURIComponent(token);
      const res = await fetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const blob = await res.blob();
      // Prefer the filename the server sets; fall back to a club slug.
      const disp = res.headers.get('content-disposition') || '';
      const m = disp.match(/filename\*?=(?:UTF-8'')?"?([^\";]+)"?/i);
      const slug = (state.club || 'team-order').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const ext = blob.type.indexOf('spreadsheet') !== -1 ? '.xlsx' : '.csv';
      const a = el('a', {
        href: URL.createObjectURL(blob),
        download: m ? decodeURIComponent(m[1]) : ('team-order-' + slug + ext)
      });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (err) {
      notice('Could not download the template: ' + (err.message || 'unknown error') + '. Please try again.', 'error');
    } finally {
      btn.textContent = label;
    }
  });

  const drop = $('[data-tofm-drop]');
  const csvInput = $('[data-tofm-csv-input]');
  if (drop && csvInput) {
    ['dragenter', 'dragover'].forEach(ev => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-drag'); }));
    ['dragleave', 'drop'].forEach(ev => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-drag'); }));
    drop.addEventListener('drop', (e) => {
      const f = e.dataTransfer.files[0];
      if (f) handleCsv(f);
    });
    csvInput.addEventListener('change', () => {
      if (csvInput.files[0]) handleCsv(csvInput.files[0]);
    });
  }
  let pendingCsvRows = [];
  // RFC 4180: handles quoted fields containing commas and escaped quotes,
  // which a plain split(',') silently corrupts.
  function parseCsv(text) {
    const rows = [];
    let row = [], cell = '', q = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (q) {
        if (c === '"') {
          if (text[i + 1] === '"') { cell += '"'; i++; }
          else q = false;
        } else cell += c;
      } else if (c === '"') q = true;
      else if (c === ',') { row.push(cell); cell = ''; }
      else if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
      else if (c !== '\r') cell += c;
    }
    if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
    return rows.filter(r => r.some(v => String(v).trim() !== ''));
  }

  // Map a sheet row onto the API shape: named fields plus items[] with
  // per-product size and add-ons.
  function rowFromSheet(rec, i) {
    const get = (k) => {
      const hit = Object.keys(rec).find(h => h.toLowerCase() === k.toLowerCase());
      return hit ? String(rec[hit]).trim() : '';
    };
    const out = {
      idx: i + 1,
      player_name: get('Player Name'),
      age_group: get('Age Group'),
      sponsor_choice: get('Sponsor'),
      items: []
    };
    state.products.forEach(p => {
      const size = get(p.label + ' Size');
      const addons = {};
      productAddons(p).forEach(a => {
        const v = get(addonColumn(p, a)) || get(p.label + ' ' + a.label);
        if (!v) return;
        if (a.type === 'bool') {
          if (/^(y|yes|true|1|x)$/i.test(v)) addons[a.key] = true;
        } else addons[a.key] = v;
      });
      if (size || Object.keys(addons).length) {
        const entry = { product: p.key, size: size };
        if (Object.keys(addons).length) entry.addons = addons;
        out.items.push(entry);
      }
    });
    // Fall back to a single Initials column if the sheet uses one.
    const flat = get('Initials');
    if (flat) out.player_initials = flat;
    const notes = get('Notes');
    if (notes) out.notes = notes;
    return out;
  }

  async function handleCsv(file) {
    if (file.size > 500 * 1024) { notice('File too large (max 500KB).', 'error'); return; }
    const name = file.name.toLowerCase();
    if (!/\.(csv|xlsx|xls)$/.test(name)) { notice('Please upload a CSV, XLSX or XLS file.', 'error'); return; }
    let headers = [], rows = [];
    if (name.endsWith('.csv')) {
      const grid = parseCsv(await file.text());
      if (!grid.length) { notice('That file looks empty.', 'error'); return; }
      headers = grid.shift().map(h => h.replace(/^\ufeff/, '').trim());
      const recs = grid.map(cells => {
        const rec = {};
        headers.forEach((h, hi) => rec[h] = (cells[hi] || '').trim());
        return rec;
      }).filter(r => (r['Player Name'] || '').trim());
      if (!recs.length) {
        notice('No player rows found — check the Player Name column is filled in.', 'error');
        return;
      }
      rows = recs.map(rowFromSheet);
      // Preview shows the sheet as read, so the captain can spot a bad column.
      rows.forEach((r, i) => { r._preview = recs[i]; });
    } else {
      // .xlsx / .xls — parsed server-side by Cian's app.
      headers = ['(server-side parse)'];
      rows = [{ note: file.name + ' will be parsed by the server on upload.' }];
      pendingCsvFile = file;
    }
    const preview = $('[data-tofm-csv-preview]');
    preview.innerHTML = '';
    const notice_ok = el('div', { class: 'tofm__notice tofm__notice--success' }, `${rows.length} row(s) parsed. Choose how to save below.`);
    preview.append(notice_ok);
    const tbl = el('table', { class: 'tofm__roster' });
    tbl.innerHTML = '<thead><tr>' + headers.map(h => `<th>${h}</th>`).join('') + '</tr></thead>';
    const tb = el('tbody');
    rows.forEach(r => {
      const tr = el('tr');
      const src = r._preview || r;
      headers.forEach(h => tr.append(el('td', null, src[h] || r.note || '')));
      tb.append(tr);
    });
    tbl.append(tb);
    preview.append(tbl);
    preview.hidden = false;
    pendingCsvRows = rows;
    $('[data-tofm-csv-actions]').hidden = false;
    $('[data-tofm-csv-help]').hidden = false;
  }
  let pendingCsvFile = null;

  // _preview is display-only scaffolding; never send it to the API.
  function csvPayload() {
    return pendingCsvRows.map(r => {
      const c = Object.assign({}, r);
      delete c._preview;
      return c;
    });
  }

  async function saveCsv(mode) {
    // mode = 'append' → POST /csv (server-side append), 'replace' → POST /rows/bulk
    if (!pendingCsvRows.length && !pendingCsvFile) { notice('Upload a sheet first.', 'error'); return; }
    const appendBtn  = $('[data-tofm-csv-append]');
    const replaceBtn = $('[data-tofm-csv-replace]');
    appendBtn.disabled = replaceBtn.disabled = true;
    try {
      if (mode === 'append') {
        if (pendingCsvFile) {
          const fd = new FormData();
          fd.append('file', pendingCsvFile);
          await api('/public/team-order/csv', { method: 'POST', body: fd, _multipart: true });
        } else {
          await api('/public/team-order/csv', { method: 'POST', body: JSON.stringify({ rows: csvPayload() }) });
        }
        notice(`Added ${pendingCsvRows.length} row(s) to the team order.`, 'success');
      } else {
        const res = await api('/public/team-order/rows/bulk', { method: 'POST', body: JSON.stringify({ rows: csvPayload() }) });
        notice(`Team order replaced with ${pendingCsvRows.length} row(s).`, 'success');
        if (res && res.rows) state.rows = res.rows;
      }
      if (!state.rows.length) state.rows = csvPayload();
      pendingCsvRows = [];
      pendingCsvFile = null;
      $('[data-tofm-csv-actions]').hidden = true;
    } catch (e) {
      if (e.status === 409) notice('Order is locked — MOU already signed.', 'warn');
      else notice('Save failed: ' + (e.message || 'unknown error'), 'error');
    } finally {
      appendBtn.disabled = replaceBtn.disabled = false;
    }
  }
  on('[data-tofm-csv-append]', 'click', () => saveCsv('append'));
  on('[data-tofm-csv-replace]', 'click', () => saveCsv('replace'));

  // ── MOU + bank + submit ──────────────────────────────
  function MOU_TEMPLATE(s) {
    return `
      <h4>MEMORANDUM OF UNDERSTANDING — GRG SPORTS TEAM ORDER</h4>
      <p><strong>Between:</strong> GRG Sports Limited, Unit 6, Ballaghaderreen Business Park,
        Ballaghaderreen, Co. Roscommon, F45 TK22 &nbsp;<strong>and</strong> ${s.club}, represented by the signing captain.</p>
      <p><strong>1. Order scope.</strong> GRG will produce the bespoke teamwear listed in the roster attached
        (deadline ${s.deadline}), personalised with the initials, sizes and sponsor branding recorded per row.</p>
      <p><strong>2. Bespoke, non-returnable.</strong> All items are custom-made to the club's specification.
        Bespoke goods are exempt from the standard 14-day right of return. No refund or exchange is
        available on this order once production has started, except for manufacturing defect.</p>
      <p><strong>3. Deposit.</strong> A 50% deposit becomes payable within 48 hours of GRG issuing the deposit
        invoice. Production begins on receipt. The remaining 50% is invoiced on completion, payable
        before dispatch.</p>
      <p><strong>4. Deadline & delivery.</strong> GRG will make its best effort to meet ${s.deadline}. Delays
        caused by external couriers or force majeure are not GRG's liability.</p>
      <p><strong>5. Manufacturing defect.</strong> Defective items are replaced or refunded at GRG's option
        within 60 days of delivery.</p>
      <p><strong>6. Sponsor branding.</strong> The club warrants it has the right to display the sponsor logos
        listed and indemnifies GRG for any unauthorised-use dispute.</p>
      <p><strong>7. Data.</strong> Stored under Irish/EU data-protection law. See
        <a href="/pages/privacy-policy">Privacy Policy</a>.</p>
      <p style="color:#b86200;"><strong>Provisional wording — pending legal review.</strong></p>
    `;
  }

  function renderMou() {
    $('[data-tofm-mou-text]').innerHTML = MOU_TEMPLATE(state);
  }

  // Payment confirmation attachment — replaces the typed IBAN. Held here
  // until submit so it goes up with the bank details in one request.
  let proofFile = null;
  const PROOF_MAX = 5 * 1024 * 1024;

  function setProof(file) {
    if (!file) return;
    if (!/^image\/|^application\/pdf$/.test(file.type)) {
      notice('Please attach an image or PDF.', 'error'); return;
    }
    if (file.size > PROOF_MAX) {
      notice('That file is too large (max 5MB).', 'error'); return;
    }
    proofFile = file;
    $('[data-tofm-proof-name]').textContent = file.name;
    $('[data-tofm-proof-preview]').hidden = false;
    $('[data-tofm-proof-drop]').hidden = true;
    notice('', 'info');
  }

  const proofInput = $('[data-tofm-proof-input]');
  if (proofInput) {
    proofInput.addEventListener('change', () => setProof(proofInput.files[0]));
  }
  const proofDrop = $('[data-tofm-proof-drop]');
  if (proofDrop) {
    ['dragenter', 'dragover'].forEach(ev => proofDrop.addEventListener(ev, (e) => {
      e.preventDefault(); proofDrop.classList.add('is-drag');
    }));
    ['dragleave', 'drop'].forEach(ev => proofDrop.addEventListener(ev, (e) => {
      e.preventDefault(); proofDrop.classList.remove('is-drag');
    }));
    proofDrop.addEventListener('drop', (e) => setProof(e.dataTransfer.files[0]));
  }
  on('[data-tofm-proof-clear]', 'click', () => {
    proofFile = null;
    if (proofInput) proofInput.value = '';
    $('[data-tofm-proof-preview]').hidden = true;
    $('[data-tofm-proof-drop]').hidden = false;
  });

  // ── Delivery vs collection ───────────────────────────
  // Address fields only appear (and are only required) for delivery.
  function fulfilment() {
    const sel = $('[data-tofm-fulfilment]:checked');
    return sel ? sel.value : 'pickup';
  }

  function syncFulfilment() {
    const wrap = $('[data-tofm-delivery]');
    if (wrap) wrap.hidden = fulfilment() !== 'delivery';
    // Class mirrors :has(input:checked) for browsers without :has support.
    $$('[data-tofm-fulfilment]').forEach(r => {
      const opt = r.closest('.tofm__fulfil-opt');
      if (opt) opt.classList.toggle('is-selected', r.checked);
    });
  }

  $$('[data-tofm-fulfilment]').forEach(r => r.addEventListener('change', syncFulfilment));
  syncFulfilment();

  function addressPayload() {
    const out = {};
    $$('[data-tofm-addr]').forEach(inp => {
      const v = inp.value.trim();
      if (v) out[inp.dataset.tofmAddr] = v;
    });
    return out;
  }

  on('[data-tofm-submit]', 'click', async () => {
    const agree = $('[data-tofm-mou-agree]').checked;
    const name  = $('[data-tofm-mou-name]').value.trim();
    const bankName = $('[data-tofm-bank-name]').value.trim();
    const bankRef  = $('[data-tofm-bank-ref]').value.trim();
    if (!agree) { notice('Please tick the MOU agreement.', 'error'); return; }
    if (name.length < 2) { notice('Please type your full name.', 'error'); return; }
    if (!bankName) { notice('Please enter the account name.', 'error'); return; }
    const method = fulfilment();
    const addr = addressPayload();
    if (method === 'delivery') {
      // Only the parts GRG genuinely needs to ship: everything else is optional.
      const missing = ['recipient', 'line1', 'city', 'county', 'eircode', 'phone']
        .filter(k => !addr[k]);
      if (missing.length) {
        notice('Please complete the delivery address — missing: ' + missing.join(', ') + '.', 'error');
        return;
      }
    }
    // Optional: the captain may sign before paying. Proof is attached here
    // only if they've already transferred the deposit.
    try {
      await api('/public/team-order/mou',  { method: 'POST', body: JSON.stringify({ agree: true, signed_name: name }) });
      // Multipart: the attachment rides along with the bank fields.
      const fd = new FormData();
      fd.append('account_name', bankName);
      fd.append('ref', bankRef);
      fd.append('fulfilment_method', method);
      // Address travels as JSON inside the multipart body so the shape stays
      // structured rather than a dozen loose form fields.
      if (method === 'delivery') fd.append('delivery_address', JSON.stringify(addr));
      if (proofFile) fd.append('proof', proofFile, proofFile.name);
      await api('/public/team-order/bank', { method: 'POST', body: fd, _multipart: true });
      notice('Order signed & locked. GRG will invoice your 50% deposit within 48 hours.', 'success');
      $('[data-tofm-submit]').disabled = true;
      $('[data-tofm-submit]').textContent = 'Submitted ✓';
    } catch (e) {
      notice('Something went wrong. Please try again or contact GRG.', 'error');
    }
  });

  // ── FILL-FORM wizard step navigation ─────────────────
  function goStep(n) {
    $$('[data-tofm-wizard-panel]').forEach(p => p.hidden = p.dataset.tofmWizardPanel !== String(n));
    // All steps are always shown. A step is "done" once the captain has
    // moved past it; reaching the final step (Done) completes every step.
    // Connector lines turn green only after a done step (see team-order.css).
    const steps = $$('[data-tofm-wizard-nav] .tofm__wizard-step');
    const last = steps.length;
    steps.forEach(li => {
      const step = Number(li.dataset.step);
      const done = step < n || n === last;
      li.hidden = false;
      li.classList.toggle('is-done', done);
      li.classList.toggle('is-active', step === n && !done);
      // The connector line is drawn off this class, so the final step
      // never trails a dangling line.
      li.classList.toggle('is-last-shown', step === last);
    });
    if (n === 2) renderFillReview();
    if (n === 3) $('[data-tofm-mou]').hidden = false;
  }

  function renderFillReview() {
    const t = $('[data-tofm-fill-review]');
    if (!t) return;
    if (modalIndex >= 0) collectModal();
    const esc = (v) => String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    let html = '<thead><tr><th style="width:44px;">#</th><th>Name</th><th>Initials</th>';
    state.products.forEach(p => { html += '<th>' + esc(p.label) + '</th>'; });
    html += '</tr></thead><tbody>';
    if (!state.draft.length) {
      html += '<tr><td colspan="' + (3 + state.products.length) + '" style="text-align:center;color:#a0a3ac;padding:24px;">No players yet — go back and add some.</td></tr>';
    } else {
      state.draft.forEach((r, i) => {
        html += '<tr><td>' + (i + 1) + '</td><td>' + (esc(r.player_name) || '—') + '</td><td>' + (esc(r.player_initials) || '—') + '</td>';
        state.products.forEach(p => {
          const it = itemFor(r, p);
          if (it && it.size) {
            // Show chosen add-ons under the size so nothing is hidden at review.
            const ad = it.addons || {};
            const bits = Object.keys(ad).map(k => {
              const a = productAddons(p).find(x => x.key === k);
              const lbl = a ? a.label : k;
              return ad[k] === true ? esc(lbl) : esc(lbl) + ': ' + esc(ad[k]);
            });
            html += '<td>' + esc(it.size)
              + (bits.length ? '<br><span class="tofm__review-addons">' + bits.join('<br>') + '</span>' : '')
              + '</td>';
          } else {
            html += '<td><span style="color:#c0392b;">— missing —</span></td>';
          }
        });
        html += '</tr>';
      });
    }
    html += '</tbody>';
    t.innerHTML = html;
  }

  root.addEventListener('click', (ev) => {
    const next = ev.target.closest('[data-tofm-wizard-next]');
    if (next) { goStep(Number(next.dataset.tofmWizardNext)); return; }
    const back = ev.target.closest('[data-tofm-wizard-back]');
    if (back) { goStep(Number(back.dataset.tofmWizardBack)); return; }
    const scr = ev.target.closest('[data-tofm-scroll-to-mou]');
    if (scr) { $('[data-tofm-mou]').scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
  });

  const confirmBtn = $('[data-tofm-fill-confirm]');
  if (confirmBtn) confirmBtn.addEventListener('click', async () => {
    // Reuse Save Draft logic then advance to step 3
    $('[data-tofm-fill-save]').click();
    goStep(3);
  });

  // ── PLAYER VIEW ──────────────────────────────────────
  function hydratePlayer(row, order) {
    $('[data-tofm-club]').textContent = (order.club_name || 'Team Order') + ' — Your row';
    $('[data-tofm-player-club]').textContent     = order.club_name || '—';
    $('[data-tofm-player-idx]').textContent      = row.idx != null ? '#' + row.idx : '—';
    $('[data-tofm-player-deadline]').textContent = order.deadline_at || '—';

    // Prefill any existing values
    ['player_name', 'player_initials', 'jersey_number', 'age_group'].forEach(f => {
      const inp = $(`[data-tofm-player-field="${f}"]`);
      if (inp) inp.value = row[f] || '';
    });

    // Sponsors
    if (order.sponsors && order.sponsors.length) {
      $('[data-tofm-player-sponsor-wrap]').hidden = false;
      const sel = $('[data-tofm-player-sponsor]');
      sel.innerHTML = '<option value="">Choose sponsor…</option>' +
        order.sponsors.map(s => `<option value="${s}"${s === row.sponsor_choice ? ' selected' : ''}>${s}</option>`).join('');
    }

    // Products → one dropdown each
    const wrap = $('[data-tofm-player-products]');
    wrap.innerHTML = '';
    (order.products || []).forEach(p => {
      const current = (row.items || []).find(it => it.product === p.key || it.product === p.label);
      const field = document.createElement('div');
      field.className = 'tofm__field';
      field.innerHTML =
        '<span class="tofm__label">' + p.label + '</span>' +
        '<select class="tofm__input" data-tofm-player-product="' + p.key + '" data-tofm-player-product-label="' + p.label + '">' +
        '<option value="">Choose size…</option>' +
        p.sizes.map(s => `<option value="${s}"${current && current.size === s ? ' selected' : ''}>${s}</option>`).join('') +
        '</select>';
      wrap.appendChild(field);
    });

    // Store products on state so validation can use them
    state.products = order.products || state.products;

    // Captain-level choices, read-only, for the player's context.
    renderAgreed('[data-tofm-player-agreed]', '[data-tofm-player-agreed-wrap]');
  }

  // ── SHARED JOIN LINK ─────────────────────────────────
  // One URL for the whole squad. The player fills the same form as a
  // personal link, but the row is claimed on submit rather than pre-assigned.
  function hydrateJoin(order) {
    hydratePlayer({}, order);
    $('[data-tofm-club]').textContent = (order.club_name || 'Team Order') + ' — Add your details';
    // No row exists yet, so there is no row number to show.
    const idxBlock = $('[data-tofm-player-idx]');
    if (idxBlock && idxBlock.parentElement) idxBlock.parentElement.hidden = true;
    const left = order.slots_remaining;
    if (typeof left === 'number') {
      notice(left > 0
        ? left + ' place(s) left in the squad.'
        : 'The squad list is full — contact your captain.', left > 0 ? 'info' : 'warn');
      if (left <= 0) {
        const f = $('[data-tofm-player-form]');
        if (f) f.hidden = true;
      }
    }
    const btn = $('[data-tofm-player-submit]');
    if (btn) btn.textContent = 'Add me to the squad';
  }

  const playerSubmit = $('[data-tofm-player-submit]');
  if (playerSubmit) playerSubmit.addEventListener('click', async () => {
    const name = $('[data-tofm-player-field="player_name"]').value.trim();
    if (name.length < 2) { notice('Please enter your full name.', 'error'); return; }
    const initials = $('[data-tofm-player-field="player_initials"]').value.trim();
    // Must match the server rule: letters, numbers and hyphens only — no dots
    // or spaces. Catch it here so the player sees it before a failed save.
    if (initials && !/^[A-Za-z0-9-]{1,6}$/.test(initials)) {
      notice('Initials can only use letters, numbers and hyphens (max 6) — no dots or spaces.', 'error');
      return;
    }

    const items = [];
    $$('[data-tofm-player-product]').forEach(sel => {
      if (sel.value) items.push({ product: sel.dataset.tofmPlayerProduct, size: sel.value });
    });
    if (state.products.length && !items.length) { notice('Please pick a size for at least one garment.', 'error'); return; }

    const data = {
      player_name: name,
      player_initials: initials,
      jersey_number: $('[data-tofm-player-field="jersey_number"]').value.trim(),
      age_group: $('[data-tofm-player-field="age_group"]').value,
      sponsor_choice: ($('[data-tofm-player-field="sponsor_choice"]') || {}).value || '',
      items: items
    };
    playerSubmit.disabled = true;
    playerSubmit.textContent = 'Saving…';
    // Unclaimed shared link -> claim a row; otherwise update the known row.
    const claiming = !!(JOIN && !ROW);
    try {
      const res = await api(claiming ? '/public/team-order/join' : '/public/team-order/row',
                            { method: 'POST', body: JSON.stringify(data) });
      if (claiming && res && res.row && res.row.row_token) {
        // Remember this player's private token so a revisit edits their row
        // rather than claiming a second one.
        try { window.localStorage.setItem(JOIN_STORE, res.row.row_token); } catch (_) {}
      }
      $('[data-tofm-player-form]').hidden = true;
      $('[data-tofm-player-done]').hidden = false;
      notice('', 'info');
    } catch (e) {
      if (e.status === 409) {
        const why = (e.message || '').indexOf('full') !== -1
          ? 'The squad list is full — contact your captain.'
          : 'This order is locked — your captain has signed the MOU.';
        notice(why, 'warn');
      } else {
        notice('Could not save: ' + (e.message || 'unknown error'), 'error');
      }
      playerSubmit.disabled = false;
      playerSubmit.textContent = claiming ? 'Add me to the squad' : 'Save my details';
    }
  });

  boot();
})();
